<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import type { ProfilPreviewModalHeaderProps } from "@/components/modal/Profile/previewProfile/types";
import type { TabItemProps } from "@/dsComponents/Tab/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Tab from "@/dsComponents/Tab/Tab.vue";

// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const props = withDefaults(defineProps<ProfilPreviewModalHeaderProps>(), {
    displayAside: true,
});

const { t } = useI18n();

const emit = defineEmits<{
    (e: "previous"): void;
    (e: "next"): void;
    (e: "openProfile"): void;
}>();

const tabIds = {
    PROFIL: "profil",
    FOLLOWING: "following",
};

const tabItems = ref<TabItemProps[]>([
    { id: tabIds.PROFIL, label: t("modals.profilPreview.tabs.profil"), icon: "user-circle", isActive: true },
    { id: tabIds.FOLLOWING, label: t("modals.profilPreview.tabs.following"), icon: "activity" },
]);

const handleTabSelect = (tabId: string) => {
    tabItems.value = tabItems.value.map((tab) => ({ ...tab, isActive: tab.id === tabId }));
};

const isTabActive = (tabId: string) => {
    return tabItems.value.find((tab) => tab.id === tabId)?.isActive ?? false;
};

defineExpose({
    isTabActive,
    tabIds,
});
</script>

<template>
    <div class="profil-preview-modal-header">
        <div class="profil-preview-modal-header__content">
            <div class="profil-preview-modal-header__content__actions-left">
                <div class="profil-preview-modal-header__content__actions-left__pagination typo-xs-medium">
                    {{ `${currentProfileIndex} / ${totalProfiles}` }}
                </div>
                <ButtonAlt
                    iconOnly="chevron-up"
                    size="sm"
                    type="neutral"
                    :disabled="currentProfileIndex === 1"
                    data-bot="previous-profile"
                    @click="emit('previous')"
                />
                <ButtonAlt
                    iconOnly="chevron-down"
                    size="sm"
                    type="neutral"
                    :disabled="currentProfileIndex === totalProfiles"
                    data-bot="next-profile"
                    @click="emit('next')"
                />
            </div>
            <div class="profil-preview-modal-header__content__title typo-sm-medium">
                {{ profileName }}
            </div>
            <div class="profil-preview-modal-header__content__actions-right">
                <ButtonAlt
                    v-for="action in actions"
                    :key="action.button.label"
                    v-bind="action.button"
                    @click="currentProfile && action.onClick?.(currentProfile)"
                />
            </div>
        </div>

        <div v-if="displayAside" class="profil-preview-modal-header__aside">
            <div class="profil-preview-modal-header__aside__tab">
                <Tab :tabGroups="[{ tabItems: tabItems }]" :borderBottom="false" @select:tab="handleTabSelect" />
            </div>

            <ButtonAlt
                :label="t('modals.profilPreview.actions.openProfil')"
                :disabled="!currentProfile?.id"
                size="sm"
                type="neutral"
                variant="stroke"
                leftIcon="arrow-up-right"
                data-bot="open-profile"
                @click="emit('openProfile')"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
.profil-preview-modal-header {
    display: flex;
    align-items: stretch;
    height: 48px;
    background-color: var(--bg-white-0, #ffffff);
    border-bottom: 1px solid var(--stroke-soft-200, #dbdfe6);

    &__content {
        display: flex;
        align-items: center;
        flex: 1 0 0;
        gap: var(--spacing-xl, 16px);
        padding: 0 var(--spacing-3xl, 24px);
        background-color: var(--bg-white-0, #ffffff);

        &__actions-left {
            display: flex;
            align-items: center;
            gap: var(--spacing-xs, 4px);

            &__pagination {
                color: var(--neutral-700, #323848);
            }
        }

        &__title {
            flex: 1 0 0;
        }

        &__actions-right {
            display: flex;
            align-items: center;
            gap: var(--spacing-md, 8px);
        }
    }

    &__aside {
        width: 420px;
        display: flex;
        align-items: center;
        gap: var(--spacing-md, 8px);
        flex-shrink: 0;
        padding: 0 var(--spacing-3xl, 24px);
        border-left: 1px solid var(--stroke-soft-200, #dbdfe6);

        &__tab {
            display: flex;
            align-items: flex-end;
            flex: 1 0 0;
            height: 48px;

            :deep(.ds-tab) {
                padding: 0;
            }
        }
    }
}
</style>
